/**
 * Auth Integration Layer
 * Connects SDK Service to Zustand Store
 */
import { toast } from 'sonner';
import i18n from 'i18next';
import { useAuthStore } from '../../store/auth.store.js';
import { useTenantStore } from '../../store/tenant.store.js';
import { Logger } from '@/lib/app-log.js';
import { authFailureInvalidatesSession } from '@/lib/auth-session-validation.js';
import { disconnectWebSocket } from '@/lib/services/ws.service.js';
import { forgotPasswordErrorCodeFromApi } from '@/lib/auth/forgot-password-send-error.js';

export class AuthIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    /**
     * Authenticate user with credentials.
     * May return `{ mfaRequired: true, ... }` without setting session.
     */
    async login(credentials) {
        const store = useAuthStore.getState();
        const tenantStore = useTenantStore.getState();

        try {
            const response = await this.service.createLogin(credentials);
            const data = response.data || {};

            if (data.mfaRequired) {
                return { success: true, mfaRequired: true, data };
            }

            if (data.passwordExpired) {
                return { success: true, passwordExpired: true, data };
            }

            const { token, refreshToken, user } = data;
            store.setAuthenticated(token, refreshToken, user);
            tenantStore.initializeFromUser(user);
            toast.success(i18n.t('toast.loginSuccess', { ns: 'login' }));

            return { success: true, data };
        } catch (error) {
            const errorKey = this._getLoginErrorKey(error);
            store.setUnauthenticated(errorKey);
            store.incrementLoginAttempts();
            toast.error(i18n.t(errorKey, { ns: 'login' }));
            return { success: false, error: errorKey };
        }
    }

    async ssoExchange(code) {
        const store = useAuthStore.getState();
        const tenantStore = useTenantStore.getState();
        try {
            const response = await this.service.ssoExchange(code);
            const data = response.data || {};
            store.setAuthenticated(data.token, data.refreshToken, data.user);
            tenantStore.initializeFromUser(data.user);
            return { success: true, data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async listPublicSsoProviders() {
        try {
            const response = await this.service.listPublicSsoProviders();
            return { success: true, data: response.data || [] };
        } catch {
            return { success: false, data: [] };
        }
    }

    async verifyMfa(payload) {
        const store = useAuthStore.getState();
        const tenantStore = useTenantStore.getState();
        try {
            const response = await this.service.verifyMfa(payload);
            const data = response.data || {};

            if (data.passwordExpired) {
                return { success: true, passwordExpired: true, data };
            }

            const { token, refreshToken, user } = data;
            store.setAuthenticated(token, refreshToken, user);
            tenantStore.initializeFromUser(user);
            toast.success(i18n.t('toast.loginSuccess', { ns: 'login' }));
            return { success: true, data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return this._otpVerifyFailure(error, message);
        }
    }

    async changeExpiredPassword(payload) {
        const store = useAuthStore.getState();
        const tenantStore = useTenantStore.getState();
        try {
            const response = await this.service.changeExpiredPassword(payload);
            const { token, refreshToken, user } = response.data || {};
            store.setAuthenticated(token, refreshToken, user);
            tenantStore.initializeFromUser(user);
            toast.success(i18n.t('toast.loginSuccess', { ns: 'login' }));
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async resendMfa(payload) {
        try {
            const response = await this.service.resendMfa(payload);
            toast.success(
                i18n.t('mfa.codeResent', { ns: 'login', defaultValue: 'Verification code resent' }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async getVerificationChannels(tenantId = null) {
        try {
            const response = await this.service.getVerificationChannels(tenantId);
            return { success: true, data: response.data || null };
        } catch (error) {
            Logger.warn('[Auth] getVerificationChannels failed', { error: error?.message });
            return { success: false, data: null };
        }
    }

    async evaluateTenantSwitch(targetTenantId) {
        try {
            const response = await this.service.evaluateTenantSwitch({ targetTenantId });
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async verifyTenantSwitch(payload) {
        try {
            const response = await this.service.verifyTenantSwitch(payload);
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return this._otpVerifyFailure(error, message);
        }
    }

    async checkAuth() {
        const store = useAuthStore.getState();
        const tenantStore = useTenantStore.getState();
        const isHydrated = useAuthStore.persist?.hasHydrated?.();

        if (!isHydrated) {
            return { success: false, pending: true };
        }

        if (!store.token) {
            if (store.status !== 'unauthenticated') {
                store.setUnauthenticated();
            }
            return { success: false };
        }

        try {
            const response = await this.service.getMe();
            const { user } = response.data;

            store.setUser(user);
            tenantStore.initializeFromUser(user);

            return { success: true, data: user };
        } catch (error) {
            const currentToken = useAuthStore.getState().token;
            const invalidSession = authFailureInvalidatesSession(error, currentToken);
            if (invalidSession) {
                store.setUnauthenticated();
            }
            Logger.warn('[Auth] Session validation failed', {
                error: error.message,
                invalidSession,
            });
            return { success: false, error: error.message, transient: !invalidSession };
        }
    }

    async refreshProfileFromServer() {
        const store = useAuthStore.getState();
        const tenantStore = useTenantStore.getState();
        if (!store.token) return { success: false };
        try {
            const response = await this.service.getMe();
            const { user } = response.data;
            store.setUser(user);
            tenantStore.initializeFromUser(user);
            return { success: true, data: user };
        } catch (error) {
            Logger.warn('[Auth] refreshProfileFromServer failed', { error: error?.message });
            return { success: false, error: error?.message };
        }
    }

    async logout() {
        const store = useAuthStore.getState();
        const tenantStore = useTenantStore.getState();

        store.setLoggingOut(true);
        try {
            disconnectWebSocket();
        } catch (e) {
            Logger.warn('[Auth] disconnectWebSocket during logout', { error: e?.message });
        }

        try {
            const refreshToken = store.refreshToken;
            await this.service.createLogout({ refreshToken });
        } catch (error) {
            Logger.error('[Auth] Server logout failed', error);
        } finally {
            store.setUnauthenticated();
            tenantStore.reset();
            try {
                localStorage.removeItem('token');
                localStorage.removeItem('refreshToken');
            } catch {
                // ignore
            }
            store.setLoggingOut(false);
            toast.success(
                i18n.t('integrationToasts:logoutSuccess', { defaultValue: 'Signed out' }),
            );
        }
    }

    async register(data) {
        const store = useAuthStore.getState();
        store.setLoading();

        try {
            const response = await this.service.createRegister(data);
            toast.success(i18n.t('toast.registerSuccess', { ns: 'login' }));
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (store.status === 'loading') {
                store.setUnauthenticated();
            }
        }
    }

    async forgotPassword({ identity, email, channel }) {
        try {
            const response = await this.service.createForgotPassword({
                identity: identity || email,
                email,
                channel,
            });
            if (response.data?.expiresInSec) {
                return { success: true, data: response.data };
            }
            // 200 without issued OTP — nothing was sent (unknown user or silent failure).
            return { success: false, code: 'NOT_ISSUED', data: null };
        } catch (error) {
            return {
                success: false,
                code: forgotPasswordErrorCodeFromApi(error),
                error: this._getErrorMessage(error),
                data: null,
            };
        }
    }

    async verifyForgotPassword(data) {
        try {
            const response = await this.service.verifyForgotPassword(data);
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return this._otpVerifyFailure(error, message);
        }
    }

    async resetPassword(data) {
        try {
            const response = await this.service.createResetPassword(data);
            toast.success(
                i18n.t('forgotPassword.resetSuccess', {
                    ns: 'login',
                    defaultValue: 'Password reset successfully',
                }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async activateAccount({ token, password, confirmPassword }) {
        try {
            const response = await this.service.createActivateAccount({
                token,
                password,
                confirmPassword,
            });
            toast.success(
                i18n.t('integrationToasts:yourAccountHasBeenActivated', {
                    defaultValue: 'Your account has been activated',
                }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async getTenantAuthOptions(tenantId) {
        try {
            const response = await this.service.getTenantAuthOptions(tenantId);
            return { success: true, data: response.data };
        } catch (error) {
            Logger.warn('[Auth] getTenantAuthOptions failed', { error: error?.message });
            return { success: false, error: error?.message };
        }
    }

    async getPartyAssignmentFlags() {
        try {
            const response = await this.service.getPartyAssignmentFlags();
            const data = response.data && typeof response.data === 'object' ? response.data : {};
            return {
                success: true,
                data: {
                    Document: Boolean(data.Document),
                    Asset: Boolean(data.Asset),
                    Risk: Boolean(data.Risk),
                },
            };
        } catch (error) {
            Logger.warn('[Auth] getPartyAssignmentFlags failed', { error: error?.message });
            return { success: false, data: { Document: false, Asset: false, Risk: false } };
        }
    }

    async requestPasswordChangeOtp(data = {}) {
        try {
            const response = await this.service.requestPasswordChangeOtp(data);
            toast.success(
                i18n.t('settings.security.codeSent', {
                    ns: 'header',
                    defaultValue: 'Verification code sent',
                }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async confirmPasswordChange(data) {
        try {
            const response = await this.service.confirmPasswordChange(data);
            toast.success(
                i18n.t('settings.security.passwordChanged', {
                    ns: 'header',
                    defaultValue: 'Password changed',
                }),
            );
            if (response.data?.user) {
                useAuthStore.getState().setUser(response.data.user);
            } else {
                await this.refreshProfileFromServer();
            }
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return this._otpVerifyFailure(error, message);
        }
    }

    async requestMfaToggleOtp(data) {
        try {
            const response = await this.service.requestMfaToggleOtp(data);
            toast.success(
                i18n.t('settings.security.codeSent', {
                    ns: 'header',
                    defaultValue: 'Verification code sent',
                }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async confirmMfaToggle(data) {
        try {
            const response = await this.service.confirmMfaToggle(data);
            toast.success(
                data.action === 'enable'
                    ? i18n.t('settings.security.mfaEnabled', {
                        ns: 'header',
                        defaultValue: 'Two-factor authentication enabled',
                    })
                    : i18n.t('settings.security.mfaDisabled', {
                        ns: 'header',
                        defaultValue: 'Two-factor authentication disabled',
                    }),
            );
            await this.refreshProfileFromServer();
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async patchMySecurity(data) {
        try {
            const response = await this.service.patchMySecurity(data);
            toast.success(
                i18n.t('settings.security.settingsSaved', {
                    ns: 'header',
                    defaultValue: 'Security settings saved',
                }),
            );
            if (response.data?.user) {
                useAuthStore.getState().setUser(response.data.user);
            } else {
                await this.refreshProfileFromServer();
            }
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    _httpStatus(error) {
        return error?.statusCode ?? error?.status ?? error?.response?.status;
    }

    _getLoginErrorKey(error) {
        const status = this._httpStatus(error);
        if (status === 403 && error?.code === 'LDAP_NOT_PROVISIONED') {
            return 'errors.ldapNotProvisioned';
        }
        if (status === 403 && error?.code === 'ACCOUNT_SUSPENDED') {
            return 'errors.accountSuspended';
        }
        if (status === 403 && error?.code === 'ACCOUNT_INACTIVE') {
            return 'errors.accountInactive';
        }
        if (status === 403 && error?.code === 'SSO_CODE_INVALID') {
            return 'errors.ssoFailed';
        }
        if (status === 401) {
            return 'errors.invalidCredentials';
        }
        if (status === 429) {
            return 'errors.rateLimit';
        }
        return 'errors.generic';
    }

    _otpVerifyFailure(error, message) {
        const details = error?.details && typeof error.details === 'object' ? error.details : {};
        return {
            success: false,
            error: message,
            ...(typeof details.attemptsRemaining === 'number'
                ? {
                    attemptsRemaining: details.attemptsRemaining,
                    maxAttempts: details.maxAttempts,
                }
                : {}),
        };
    }

    _getErrorMessage(error) {
        const status = this._httpStatus(error);
        if (status === 401) {
            return i18n.t('errors.invalidCredentials', { ns: 'login' });
        }
        if (status === 429) {
            return i18n.t('errors.rateLimit', { ns: 'login' });
        }
        return (
            error?.message
            || i18n.t('errors.unexpected', { ns: 'login', defaultValue: 'An unexpected error occurred' })
        );
    }
}
